import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface ReCaptchaContextType {
  isEnabled: boolean;
  isLoading: boolean;
  error: Error | null;
}

const ReCaptchaContext = createContext<ReCaptchaContextType | undefined>(undefined);

export function ReCaptchaProvider({ children }: { children: React.ReactNode }) {
  const [isEnabled, setIsEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  useEffect(() => {
    let mounted = true;
    let retryTimeout: NodeJS.Timeout;

    const fetchConfig = async () => {
      try {
        console.log('Fetching ReCaptcha configuration...');
        setIsLoading(true);
        setError(null);
        
        const { data, error } = await supabase
          .from('config')
          .select('value')
          .eq('key', 'recaptcha_enabled')
          .maybeSingle();

        if (!mounted) return;

        if (error) {
          console.error('Failed to fetch recaptcha_enabled config:', {
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          throw error;
        }

        // If no config found, default to true for security
        if (!data) {
          console.warn('No recaptcha_enabled config found in database, defaulting to enabled');
          setIsEnabled(true);
          setIsLoading(false);
          return;
        }

        const enabled = data.value === 'true';
        console.log('ReCaptcha config found:', { enabled });
        setIsEnabled(enabled);
        setRetryCount(0); // Reset retry count on success
      } catch (err) {
        console.error('Error fetching recaptcha config:', {
          name: err instanceof Error ? err.name : 'Unknown',
          message: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined
        });

        if (!mounted) return;

        setError(err instanceof Error ? err : new Error('Failed to fetch ReCaptcha config'));

        // Retry with exponential backoff
        if (retryCount < maxRetries) {
          const nextRetry = retryCount + 1;
          const delay = Math.min(1000 * Math.pow(2, nextRetry), 10000);
          
          console.log(`Retrying fetch config in ${delay}ms (attempt ${nextRetry}/${maxRetries})`);
          
          retryTimeout = setTimeout(() => {
            if (mounted) {
              setRetryCount(nextRetry);
              fetchConfig();
            }
          }, delay);
        } else {
          console.error('Failed to fetch config after maximum retries');
          // Default to true for security
          setIsEnabled(true);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchConfig();

    return () => {
      mounted = false;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [retryCount]);

  useEffect(() => {
    console.log('ReCaptchaContext state:', { isEnabled, isLoading, error });
  }, [isEnabled, isLoading, error]);

  const value = {
    isEnabled,
    isLoading,
    error
  };

  return (
    <ReCaptchaContext.Provider value={value}>
      {children}
    </ReCaptchaContext.Provider>
  );
}

export function useReCaptchaContext() {
  const context = useContext(ReCaptchaContext);
  if (context === undefined) {
    throw new Error('useReCaptchaContext must be used within a ReCaptchaProvider');
  }
  return context;
}